import { Button } from '../components/Button';
import { Spinner } from '../components/Spinner';
import { navigate } from './routes';
import { useAppStore } from './store';
import { ThemeToggle } from './ThemeToggle';

/**
 * The top bar: the active plan's name + persistence-mode indicator (offline localStorage vs synced
 * to the API), a Run button (kick off a client-side simulation → navigate to Results), Save (persist
 * via the plan store), and the theme toggle. The Run/Save actions live here so they are reachable
 * from every screen, and both surface their async status (busy spinner + an inline error/saved hint).
 *
 * The actions delegate to the store thunks (`runActive` / `saveActive`) — the app-shell seam over
 * the data layer — so the wiring is identical no matter which persistence backend / data source is
 * active.
 */
export function TopBar(): React.ReactElement {
  const plan = useAppStore((s) => s.plan);
  const mode = useAppStore((s) => s.store.mode);
  const runStatus = useAppStore((s) => s.runStatus);
  const runError = useAppStore((s) => s.runError);
  const saveStatus = useAppStore((s) => s.saveStatus);
  const saveError = useAppStore((s) => s.saveError);
  const runActive = useAppStore((s) => s.runActive);
  const saveActive = useAppStore((s) => s.saveActive);

  const personName = plan.household.people[0]?.name ?? 'Untitled plan';
  const running = runStatus === 'running';
  const saving = saveStatus === 'saving';

  const onRun = (): void => {
    void runActive(() => navigate('results'));
  };
  const onSave = (): void => {
    void saveActive();
  };

  return (
    <header className="topbar">
      <div className="topbar__title">
        {personName}
        <span
          className={`badge${mode === 'api' ? ' badge--pos' : ''}`}
          style={{ marginLeft: 'var(--sp-3)' }}
        >
          {mode === 'api' ? 'synced' : 'offline · local'}
        </span>
      </div>
      <div className="topbar__actions">
        {(runError !== null || saveError !== null) && (
          <span
            className="topbar__status"
            role="alert"
            style={{ color: 'var(--neg)', fontSize: 'var(--fs-small)' }}
          >
            {runError ?? saveError}
          </span>
        )}
        {saveStatus === 'saved' && saveError === null && (
          <span
            className="topbar__status"
            style={{ color: 'var(--ink-muted)', fontSize: 'var(--fs-small)' }}
          >
            Saved
          </span>
        )}
        <Button variant="default" onClick={onSave} busy={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="primary"
          onClick={onRun}
          busy={running}
          icon={running ? <Spinner size={14} /> : undefined}
        >
          {running ? 'Running…' : 'Run simulation'}
        </Button>
        <ThemeToggle />
      </div>
    </header>
  );
}
